// src/pages/pengamatan/buah-alami/BuahAlamiToolbar.jsx
import React, { useState, useEffect } from 'react';
import {
    Typography, Stack, TextField, IconButton, Menu, MenuItem, Divider, Alert, Snackbar,
    Tabs, Tab, Box
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import { styled, alpha } from '@mui/material/styles';

// Import fungsi-fungsi terpisah
import { useDraftActions } from './draftActions';
import { handleDownloadExcel } from './excelDownload';

// PERBAIKAN: Import dari file yang benar
import { useDownloadForm } from './hooks/useDownloadForm'; // Import dari hooks folder
import DownloadFormDialog from './DownloadFormDialog'; // Import komponen UI

import { useProcessedDialog } from './hooks/useProcessedDialog';
import ProcessedConfirmationDialog from './ProcessedConfirmationDialog';

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

const BuahAlamiToolbar = ({ 
  searchTerm, 
  onSearchChange, 
  anchorEl, 
  onMenuClick, 
  onMenuClose, 
  open,
  selectedItems,
  userJabatan,
  userRoleNames,
  updateStatusPengamatan,
  onStatusUpdate,
  orders,
  currentTab,
  onTabChange,
  getTabCount,
  pendingOrders = [],
  approvedOrders = [],
  processedOrders = [],
  closingOrders = []
}) => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Gunakan hook untuk aksi draft TANPA fungsi dialog konfirmasi
  const {
    showDraftMenu,
    showDownloadEntryMenu,
    allSelectedItemsAreOnlyProcessed
  } = useDraftActions({
    selectedItems,
    orders,
    updateStatusPengamatan,
    onStatusUpdate,
    onMenuClose,
    currentTab,
    approvedOrders,
    processedOrders,
    pendingOrders,
    closingOrders
  });

  // PERBAIKAN: Gunakan hook dari file yang benar
  const {
    downloadDialogOpen,
    selectedLocation,
    searchTerm: locationSearchTerm,
    filteredLocations,
    selectedPengamatanData,
    loading,
    handleOpenDownloadDialog,
    handleCloseDownloadDialog,
    handleSearchChange,
    handleAutocompleteChange
  } = useDownloadForm(onStatusUpdate);

  const {
    processedDialogOpen,
    draftDialogOpen,
    processingData,
    draftData,
    selectedItemsForProcessing,
    selectedItemsForDraft,
    currentTab: dialogTab,
    handleOpenProcessedDialog,
    handleOpenDraftDialog,
    handleCloseProcessedDialog,
    handleCloseDraftDialog
  } = useProcessedDialog();

  // FUNGSI BARU: Cek apakah user memiliki akses penuh
  const hasFullAccess = () => {
    const isValidJabatan = userJabatan === "Admin" || userJabatan === "Section Head";
    const isValidRole = userRoleNames.includes("Buah Alami") || userRoleNames.includes("Developer");
    
    return isValidJabatan && isValidRole;
  };

  // FUNGSI BARU: Cek apakah user memiliki akses download form
  const hasDownloadAccess = () => {
    const isValidJabatan = userJabatan === "Admin" || userJabatan === "Section Head";
    const isValidRole = userRoleNames.includes("Buah Alami") || userRoleNames.includes("Developer");
    
    return isValidJabatan && isValidRole;
  };

  // FUNGSI BARU: Cek apakah tombol MoreVertIcon harus ditampilkan
  const shouldShowMoreVertIcon = () => {
    const shouldShow = selectedItems.length > 0 && 
                     (currentTab === 'Approved' || currentTab === 'Processed' || currentTab === 'Closing');
    
    return shouldShow;
  };

  // FUNGSI BARU: Cek apakah menu "Ubah Ke Draft" harus ditampilkan
  const shouldShowDraftMenu = () => {
    return hasFullAccess() && showDraftMenu();
  };

  // FUNGSI BARU: Cek apakah menu "Ubah Ke Processed" harus ditampilkan
  const shouldShowProcessedMenu = () => {
    if (selectedItems.length === 0) return false;
    const shouldShow = currentTab === 'Approved';
    return shouldShow;
  };

  // FUNGSI BARU: Cek apakah tombol Download harus ditampilkan
  const shouldShowDownloadIcon = () => {
    return hasDownloadAccess();
  };

  // Wrapper function untuk download Excel
  const handleExcelDownload = () => {
    handleDownloadExcel(selectedItems, orders, onStatusUpdate, onMenuClose);
  };

  // FUNGSI BARU: Handle tombol Ubah Ke Processed - DENGAN DIALOG
  const handleProcessedClick = () => {
    handleOpenProcessedDialog(selectedItems, orders, currentTab);
    onMenuClose();
  };

  // FUNGSI BARU: Handle tombol Ubah Ke Draft - DENGAN DIALOG
  const handleDraftClick = () => {
    handleOpenDraftDialog(selectedItems, orders, currentTab);
    onMenuClose();
  };

  // FUNGSI BARU: Handle konfirmasi ubah status ke Processed
  const handleConfirmProcessed = async () => {
    try {
      // Panggil fungsi updateStatusPengamatan dari useBuahAlamiData
      const result = await updateStatusPengamatan(selectedItemsForProcessing, {
        action: 'Processed',
        contextTab: dialogTab
      });

      if (result.success) {
        onStatusUpdate({
          open: true,
          message: `Berhasil mengubah ${result.updatedCount} plot menjadi Processed`,
          severity: "success"
        });
        
        // HAPUS RELOAD: Tidak perlu reload karena data akan otomatis update via Firebase listener
      } else {
        onStatusUpdate({
          open: true,
          message: result.reason || `Gagal mengubah status: ${result.error}`,
          severity: "error"
        });
      }
    } catch (error) {
      console.error('Error in handleConfirmProcessed:', error);
      onStatusUpdate({
        open: true,
        message: "Terjadi kesalahan saat mengubah status: " + error.message,
        severity: "error"
      });
    } finally {
      handleCloseProcessedDialog();
    }
  };

  // FUNGSI BARU: Handle konfirmasi ubah status ke Draft
  const handleConfirmDraft = async () => {
    try {
      
      // Panggil fungsi updateStatusPengamatan dari useBuahAlamiData
      const result = await updateStatusPengamatan(selectedItemsForDraft, {
        action: 'Draft',
        contextTab: dialogTab
      });

      if (result.success) {
        onStatusUpdate({
          open: true,
          message: `Berhasil mengubah ${result.updatedCount} plot menjadi Draft`,
          severity: "success"
        });
        
        // HAPUS RELOAD: Tidak perlu reload karena data akan otomatis update via Firebase listener
      } else {
        onStatusUpdate({
          open: true,
          message: result.reason || `Gagal mengubah status: ${result.error}`,
          severity: "error"
        });
      }
    } catch (error) {
      console.error('Error in handleConfirmDraft:', error);
      onStatusUpdate({
        open: true,
        message: "Terjadi kesalahan saat mengubah status: " + error.message,
        severity: "error"
      });
    } finally {
      handleCloseDraftDialog();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" component="h2">
          Data Buah Alami
        </Typography>
        
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            placeholder="Cari berdasarkan lokasi..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={onSearchChange}
            sx={{ minWidth: 300 }}
          />
          
          {/* Tombol Download - Tampilkan hanya jika user memiliki akses download */}
          {shouldShowDownloadIcon() && (
            <IconButton
              onClick={handleOpenDownloadDialog}
              color="primary"
              title="Download Form"
            >
              <DownloadIcon />
            </IconButton>
          )}

          {/* Tombol Aksi - Tampilkan hanya jika ada checkbox yang dipilih */}
          {shouldShowMoreVertIcon() && (
            <IconButton
              onClick={onMenuClick}
              color="primary"
              title="Aksi"
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {/* Scrollable Tabs */}
      <Box 
        sx={{ 
          mb: 2,
          backgroundColor: '#f2f9ff',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Tabs
          value={currentTab}
          onChange={onTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              fontWeight: 'bold',
              minWidth: 120,
              py: 1.5,
              fontSize: '0.875rem',
              color: 'text.primary',
              '&.Mui-selected': {
                color: 'primary.main',
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
              height: 3
            }
          }}
        >
          <Tab 
            value="Pending" 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Pending
                <Box 
                  sx={{ 
                    ml: 1, 
                    backgroundColor: 'warning.main', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: 20, 
                    height: 20, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 'bold'
                  }}
                >
                  {getTabCount('Pending')}
                </Box>
              </Box>
            } 
          />
          <Tab 
            value="Approved" 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Approved
                <Box 
                  sx={{ 
                    ml: 1, 
                    backgroundColor: 'success.main', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: 20, 
                    height: 20, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 'bold'
                  }}
                >
                  {getTabCount('Approved')}
                </Box>
              </Box>
            } 
          />
          <Tab 
            value="Processed" 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Processed
                <Box 
                  sx={{ 
                    ml: 1, 
                    backgroundColor: 'info.main', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: 20, 
                    height: 20, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 'bold'
                  }}
                >
                  {getTabCount('Processed')}
                </Box>
              </Box>
            } 
          />
          <Tab 
            value="Closing" 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Closing
                <Box 
                  sx={{ 
                    ml: 1, 
                    backgroundColor: 'error.main', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: 20, 
                    height: 20, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 'bold'
                  }}
                >
                  {getTabCount('Closing')}
                </Box>
              </Box>
            } 
          />
        </Tabs>
      </Box>

      {/* Dialog Download Form PDF */}
      {hasDownloadAccess() && (
        <DownloadFormDialog
          open={downloadDialogOpen}
          selectedLocation={selectedLocation}
          searchTerm={locationSearchTerm}
          filteredLocations={filteredLocations}
          selectedPengamatanData={selectedPengamatanData}
          loading={loading}
          onClose={handleCloseDownloadDialog}
          onSearchChange={handleSearchChange}
          
          handleAutocompleteChange={handleAutocompleteChange}
        />
      )}

      {/* Menu Aksi - Tampilkan hanya jika ada item yang dipilih */}
      {shouldShowMoreVertIcon() && (
        <StyledMenu
          anchorEl={anchorEl}
          open={open}
          onClose={onMenuClose}
        >
          {/* Info selected items */}
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {selectedItems.length} item dipilih
            </Typography>
          </MenuItem>
          <Divider />
          
          {/* TAB APPROVED: Ubah ke Draft dan Ubah ke Processed */}
          {currentTab === 'Approved' && (
            <>
              {/* Menu "Ubah Ke Draft" - SEKARANG ADA FUNGSI */}
              {shouldShowDraftMenu() && (
                <MenuItem onClick={handleDraftClick} disableRipple>
                  <EditIcon />
                  Ubah Ke Draft
                </MenuItem>
              )}

              {/* Menu "Ubah Ke Processed" - SEKARANG ADA FUNGSI */}
              {shouldShowProcessedMenu() && (
                <MenuItem onClick={handleProcessedClick} disableRipple>
                  <EditIcon />
                  Ubah Ke Processed
                </MenuItem>
              )}

              {/* Untuk user tanpa akses penuh di tab Approved, tidak menampilkan menu apapun */}
              {!hasFullAccess() && (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    Tidak ada aksi yang tersedia
                  </Typography>
                </MenuItem>
              )}
            </>
          )}
          
          {/* TAB PROCESSED: Ubah ke Draft dan Download Entry */}
          {currentTab === 'Processed' && (
            <>
              {/* Menu "Ubah Ke Draft" - SEKARANG ADA FUNGSI */}
              {shouldShowDraftMenu() && (
                <MenuItem onClick={handleDraftClick} disableRipple>
                  <EditIcon />
                  Ubah Ke Draft
                </MenuItem>
              )}
              
              {/* Download Entry tersedia untuk semua user di tab Processed */}
              {showDownloadEntryMenu() && (
                <MenuItem 
                  onClick={handleExcelDownload} 
                  disableRipple
                  disabled={!allSelectedItemsAreOnlyProcessed()}
                >
                  <DownloadIcon />
                  Download Entry
                </MenuItem>
              )}

              {/* Untuk user tanpa akses penuh yang tidak bisa download */}
              {!hasFullAccess() && !showDownloadEntryMenu() && (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    Tidak ada aksi yang tersedia
                  </Typography>
                </MenuItem>
              )}
            </>
          )}
          
          {/* TAB CLOSING: Hanya Download Entry */}
          {currentTab === 'Closing' && (
            <>
              {/* Download Entry tersedia untuk semua user di tab Closing */}
              {showDownloadEntryMenu() && (
                <MenuItem 
                  onClick={handleExcelDownload} 
                  disableRipple
                  disabled={!allSelectedItemsAreOnlyProcessed()}
                >
                  <DownloadIcon />
                  Download Entry
                </MenuItem>
              )}

              {/* Untuk user yang tidak bisa download */}
              {!showDownloadEntryMenu() && (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    Tidak ada aksi yang tersedia
                  </Typography>
                </MenuItem>
              )}
            </>
          )}
          
          <Divider sx={{ my: 0.5 }} />
          
          <MenuItem onClick={onMenuClose} disableRipple>
            Batal
          </MenuItem>
        </StyledMenu>
      )}

      {/* Dialog Konfirmasi Ubah ke Processed */}
      <ProcessedConfirmationDialog
        open={processedDialogOpen}
        processingData={processingData}
        onClose={handleCloseProcessedDialog}
        onConfirm={handleConfirmProcessed}
        currentTab={dialogTab}
        actionType="processed"
      />

      {/* DIALOG BARU: Konfirmasi Ubah ke Draft */}
      <ProcessedConfirmationDialog
        open={draftDialogOpen}
        processingData={draftData}
        onClose={handleCloseDraftDialog}
        onConfirm={handleConfirmDraft}
        currentTab={dialogTab}
        actionType="draft"
      />

      {/* Snackbar untuk feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BuahAlamiToolbar;